import { checkIsPublicRoute } from '@nxtpeople/nxt-vue-keycloak';
import { NavigationGuard } from 'vue-router';
import { hasPermission } from '../helpers/scope-checker.helper';
import { publicRoutes } from '../router';
import store from '../store';

/**
 * Route guard that checks if the user has permission to access the requested page
 */
const PERMISSION_GUARD: NavigationGuard = (to, _from, next) => {
   if (checkIsPublicRoute(to, publicRoutes)) return next();

   // Skip permission check for public routes and error pages
   if (to.meta?.public || to.meta?.error) {
      next();
      return;
   }

   const userInfo = store.state.userInfo?.userInfo;

   // If user info is not loaded yet, skip permission check (auth guard should handle this)
   if (!userInfo) {
      next();
      return;
   }

   // Map route names to permission pages
   const routePermissionMap: Record<string, string> = {
      // Candidates module
      Candidates: 'candidates',
      CandidatesOverview: 'candidates',
      CandidateDetail: 'candidates',

      // Positions module
      Positions: 'positions',
      PositionsOverview: 'positions',
      PositionDetail: 'positions',
      PositionOverviewDetails: 'positions',
      PositionDetailsPositionPage: 'positions',
      PositionProfileDetailsPage: 'positions',
      PositionMatchesDetailsPage: 'positions',
      PositionPublicationsDetailsPage: 'positions',

      // Customers module
      Customers: 'customers',
      CustomersOverview: 'customers',
      CustomerDetail: 'customers',

      // Matching module
      Matching: 'matching',
      MatchingOverview: 'matching',

      // Call Lists module
      CallLists: 'call_lists',
      CallListsOverview: 'call_lists',

      // Follow Up module
      FollowUp: 'follow_up.matches',
      FollowUpOverview: 'follow_up.matches',
      FollowUpMatchesOverview: 'follow_up.matches',
      FollowUpMatchesOverviewDetail: 'follow_up.matches',
      FollowUpJourneysOverview: 'follow_up.journeys',
      FollowUpJourneysOverviewDetail: 'follow_up.journeys',
      FollowUpGeneralOverview: 'follow_up.tasks',
      FollowUpGeneralOverviewDetail: 'follow_up.tasks',

      // Communications module
      Communications: 'communications',

      // Reporting module
      Reporting: 'report',
   };

   // Get the permission page for the current route
   const routeName = to.name as string;
   const permissionPage = routePermissionMap[routeName];

   // If no permission mapping found, allow access (for sub-routes, etc.)
   if (!permissionPage) {
      next();
      return;
   }

   // Check if user has 'view' permission for this page
   if (hasPermission(userInfo, permissionPage, 'view')) {
      next();
      return;
   }

   // User doesn't have permission, redirect to forbidden page
   next({
      name: 'Forbidden',
   });
};

export default PERMISSION_GUARD;
